// REG-273/274: one hostname = one session cookie per browser, so a login
// or logout in one tab changes the org every other tab is acting as.
// Per-org subdomains used to prevent this (a cookie jar per org).
//
// Broadcast only on explicit session actions, never from a render effect
// (every page load starts with no user, which would reload every other tab,
// which would broadcast again: a storm). And decide staleness by asking the
// server, never by trusting what is in localStorage.
//
// localStorage access is wrapped: private windows, blocked site data, or a
// full quota can throw. The tab still works, just without the broadcast.

const KEY = 'regen:session-changed'

/** Call right after any action that changes this browser's session. */
export function broadcastSessionChange(): void {
  try {
    localStorage.setItem(KEY, `${Date.now()}-${Math.random()}`)
  } catch {
    // Other tabs still catch up on their next focus/visibility re-check.
  }
}

/**
 * Calls onStale when the server reports a different org than this tab
 * loaded with. getLoadedOrgId returns undefined until this tab has finished
 * loading (skip), null when it loaded signed out, else the org id.
 */
export function watchForOrgChange(
  getLoadedOrgId: () => string | null | undefined,
  fetchCurrentOrgId: () => Promise<string | null>,
  onStale: () => void
): () => void {
  let checking = false

  async function check() {
    const loaded = getLoadedOrgId()
    if (loaded === undefined || checking) return
    checking = true
    try {
      const current = await fetchCurrentOrgId()
      if (current !== loaded) onStale()
    } catch {
      // Network error: unknown, not "signed out". Do nothing.
    } finally {
      checking = false
    }
  }

  function onStorage(event: StorageEvent) {
    if (event.key === KEY) void check()
  }
  function onVisibility() {
    if (document.visibilityState === 'visible') void check()
  }

  window.addEventListener('storage', onStorage)
  document.addEventListener('visibilitychange', onVisibility)
  window.addEventListener('focus', onVisibility)
  return () => {
    window.removeEventListener('storage', onStorage)
    document.removeEventListener('visibilitychange', onVisibility)
    window.removeEventListener('focus', onVisibility)
  }
}
